import { Injectable } from '@angular/core';
import { AppConfig } from '../types/app-config.type';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';

/**
 * Service used to read and provide the config values from the config file
 */
@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  /**
   * App configuration which stores the config values to be used
   */
  private appConfig: AppConfig;

  public constructor(private httpClient: HttpClient) { }

  public loadAppConfig(): Promise<AppConfig> {
    const currentDate = new Date();
    let params;
    if (window.location.hash.split('?').length > 1) {
      params = window.location.hash.split('?')[ 1 ];
    }
    let configPath;
    if (params === 'useAzure=true' || this.getCookie('useAzure') === 'true') {
      if (this.isProduction()) {
        configPath = '/AzureConfig.json?v=';
        document.cookie = 'useAzure=true' + ';expires=Thu, 01 Jan 2030 00:00:00 UTC;domain=pearsonvue.com;path=/';
      } else {
        configPath = '/Connect/AzureConfig.json?v=';
        document.cookie = 'useAzure=true' + ';expires=Thu, 01 Jan 2030 00:00:00 UTC;domain=localhost;path=/';
      }
    } else {
      configPath = this.isProduction() ? '/config.json?v=' : '/Connect/config.json?v=';
    }

    return firstValueFrom(
      this.httpClient.get<AppConfig>(configPath + currentDate.toString())
    ).then((response) => {
      this.appConfig = response;
      return response;
    });
  }

  public getCookie(name: string): string {
    const ca = window.document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[ i ];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) !== -1) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  public getAPIUrl(): string {
    return this.appConfig.apiUrl;
  }

  public getXliffServicesUrl(): string {
    return this.appConfig.xliffUrl;
  }

  public getTestCenterServicesUrl(): string {
    return this.appConfig.testCenterServicesUrl;
  }

  public getCASUrl(): string {
    return this.appConfig.casUrl;
  }

  public getTedUrl(): string {
    return this.appConfig.tedUrl;
  }

  public getEnvironment(): string {
    return this.appConfig.env;
  }

  public isProduction(): boolean {
    return environment.production;
  }

  public getSiteMaintenanceSupportServicesUrl(): string {
    return this.appConfig.siteMaintenanceSupportServicesUrl;
  }

  public getLocaleHelpLinkEnabled(): boolean {
    return this.appConfig.localeHelpLinkEnabled;
  }

  public getWebHelpBaseURL(): string {
    return this.appConfig.webHelpBaseUrl;
  }

  public getWebHelpUrl(): string {
    if (this.getLocaleHelpLinkEnabled()) {
      let configHelpUrl: string = this.appConfig.webHelpUrl;

      if (configHelpUrl.includes('{locale}') || configHelpUrl.includes('%7Blocale%7D')) {
        configHelpUrl = this.localizeHelpUrl(configHelpUrl);
      }
      return configHelpUrl;
    }

    return this.appConfig.webHelpUrl;
  }

  public getLocaleCookieName(): string {
    return 'CONNECT_LOCALE';
  }

  public getNormalizedLocale(): string {
    const result = this.normalizeLocale(this.getCookie(this.getLocaleCookieName()));
    if (!result) {
      return 'en_us';
    }
    return result;
  }

  private normalizeLocale(locale: string): string {
    if (!locale) {
      return locale;
    }

    let trimmedLocale = locale.trim();

    if (trimmedLocale[ 0 ] === '=') {
      trimmedLocale = trimmedLocale.substring(1);
    }

    return trimmedLocale.toLowerCase().replace('-', '_');
  }

  private localizeHelpUrl(url: string): string {
    const locale = this.getNormalizedLocale();
    if (!locale) {
      return url.replace('{locale}', 'en_us').replace('%7Blocale%7D', 'en_us');
    }
    return url.replace('{locale}', locale).replace('%7Blocale%7D', locale);
  }
}
